import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import "./StockManagement.css";

/* ══════════════════════════════════════════════════
   API CONFIG
══════════════════════════════════════════════════ */
const RAW_API_BASE =
  (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) ||
  (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_API_URL) ||
  (typeof process !== "undefined" && process.env?.VITE_API_URL) ||
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) ||
  "";
const CLEAN_API_BASE = String(RAW_API_BASE || "").trim().replace(/\/+$/, "");
const API_URL = CLEAN_API_BASE
  ? CLEAN_API_BASE.endsWith("/api")
    ? CLEAN_API_BASE
    : `${CLEAN_API_BASE}/api`
  : "/api";
const STOCK_API = `${API_URL}/admin/stock`;

/* ══════════════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════════════ */
const CATEGORIES = [
  { key: "all", label: "All Items", emoji: "📦", color: "#6366f1" },
  { key: "cakes", label: "Cakes", emoji: "🎂", color: "#ec4899" },
  { key: "decorations", label: "Decorations", emoji: "🎨", color: "#f59e0b" },
  { key: "gifts", label: "Gifts", emoji: "🎁", color: "#06b6d4" },
  { key: "services", label: "Services", emoji: "🛎️", color: "#8b5cf6" },
];
const STORAGE_KEY = "stock_management_data";
const STOCK_EVENTS_KEY = "stock_events";
const DEFAULT_STOCK = [
  { id: "1", _id: "1", name: "Strawberry Dream Cake", category: "cakes", price: 1200, stock: 25, minStock: 5, emoji: "🎂" },
  { id: "2", _id: "2", name: "Chocolate Truffle Cake", category: "cakes", price: 1500, stock: 18, minStock: 5, emoji: "🎂" },
  { id: "3", _id: "3", name: "Red Velvet Cake", category: "cakes", price: 1400, stock: 3, minStock: 5, emoji: "🎂" },
  { id: "4", _id: "4", name: "Crystal Curtain", category: "decorations", price: 800, stock: 40, minStock: 10, emoji: "🎨" },
  { id: "5", _id: "5", name: "LED String Lights", category: "decorations", price: 500, stock: 60, minStock: 15, emoji: "🎨" },
  { id: "6", _id: "6", name: "Chocolate Gift Box", category: "gifts", price: 450, stock: 55, minStock: 10, emoji: "🎁" },
  { id: "7", _id: "7", name: "Photography Package", category: "services", price: 3000, stock: 15, minStock: 3, emoji: "🛎️" },
];

/* ══════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════ */
const n = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const money = (value) => `₹${n(value).toLocaleString("en-IN")}`;
const normName = (name) =>
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normalizeItem = (item) => {
  if (!item) return null;
  const idStr = String(item._id || item.id || `${Date.now()}`);
  return {
    ...item,
    id: idStr,
    _id: idStr,
    name: String(item.name || "Untitled").trim(),
    category: String(item.category || "cakes").toLowerCase(),
    price: Math.max(0, n(item.price)),
    stock: Math.max(0, Math.floor(n(item.stock))),
    minStock: Math.max(1, Math.floor(n(item.minStock || 5))),
    emoji: item.emoji || CATEGORIES.find((c) => c.key === item.category)?.emoji || "📦",
  };
};

const loadStock = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map(normalizeItem).filter(Boolean);
      }
    }
  } catch (e) {
    console.error("Failed to load stock from localStorage:", e);
  }
  return DEFAULT_STOCK.map(normalizeItem);
};

const saveStock = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error("Failed to save stock to localStorage:", e);
  }
};

const loadStockEvents = () => {
  try {
    const stored = localStorage.getItem(STOCK_EVENTS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {
    console.error("Failed to load stock events:", e);
  }
  return [];
};

const saveStockEvents = (events) => {
  try {
    localStorage.setItem(STOCK_EVENTS_KEY, JSON.stringify(events));
  } catch (e) {
    console.error("Failed to save stock events:", e);
  }
};

const getCategoryInfo = (key) =>
  CATEGORIES.find((c) => c.key === key) || CATEGORIES[1];

const eventKey = (ev) =>
  `${ev?.orderId || ""}|${ev?.timestamp || ""}|${ev?.orderName || ""}`;

/**
 * Build map: normalized item name → absolute newStock
 * from one or many deduction rows (last write wins).
 */
const buildNewStockMap = (deductedItems = []) => {
  const map = new Map();
  (deductedItems || []).forEach((d) => {
    const key = normName(d?.name);
    if (!key) return;

    if (d.newStock !== undefined && d.newStock !== null && d.newStock !== "") {
      map.set(key, Math.max(0, Math.floor(n(d.newStock))));
      return;
    }
    // qty-only rows are handled in apply step against current stock
    if (d.qty !== undefined || d.quantity !== undefined) {
      map.set(key, { __delta: Math.max(0, Math.floor(n(d.qty ?? d.quantity ?? 1))) });
    }
  });
  return map;
};

/**
 * Apply absolute newStock (preferred) or qty delta onto cards.
 * This is what makes "Current Stock" drop when Confirm is clicked.
 */
const applyDeductionsToStock = (currentStock, deductedItems = []) => {
  if (!Array.isArray(deductedItems) || deductedItems.length === 0) {
    return currentStock;
  }

  const byName = buildNewStockMap(deductedItems);
  if (byName.size === 0) return currentStock;

  let changed = false;
  const next = (currentStock || []).map((item) => {
    const key = normName(item.name);
    if (!byName.has(key)) return item;

    const val = byName.get(key);
    let nextStock;

    if (val && typeof val === "object" && val.__delta !== undefined) {
      nextStock = Math.max(0, Math.floor(n(item.stock) - n(val.__delta)));
    } else {
      nextStock = Math.max(0, Math.floor(n(val)));
    }

    if (nextStock !== n(item.stock)) changed = true;
    return { ...item, stock: nextStock };
  });

  return changed ? next : currentStock;
};

/**
 * Replay ALL events (oldest → newest). Latest newStock per name wins.
 * Used on mount so cards catch up even if user opens Stock page later.
 */
const applyAllEventsToStock = (currentStock, events = []) => {
  if (!Array.isArray(events) || events.length === 0) return currentStock;

  // Sort oldest first so later confirms overwrite earlier ones
  const ordered = [...events].sort(
    (a, b) => n(a.timestamp) - n(b.timestamp)
  );

  let next = currentStock;
  ordered.forEach((ev) => {
    if (Array.isArray(ev?.items) && ev.items.length > 0) {
      next = applyDeductionsToStock(next, ev.items);
    }
  });
  return next;
};

/**
 * After API fetch: if a recent event says stock is lower than API,
 * trust the lower value (protects against stale API / cache).
 * Events older than maxAgeMs are ignored so manual restocks still work.
 */
const mergeApiStockWithRecentEvents = (
  apiStock,
  events = [],
  maxAgeMs = 10 * 60 * 1000
) => {
  const now = Date.now();
  const recent = (events || []).filter(
    (ev) => now - n(ev.timestamp) <= maxAgeMs
  );
  if (recent.length === 0) return apiStock;

  // Latest absolute newStock per name from recent events
  const latest = new Map();
  [...recent]
    .sort((a, b) => n(a.timestamp) - n(b.timestamp))
    .forEach((ev) => {
      (ev.items || []).forEach((d) => {
        const key = normName(d.name);
        if (!key) return;
        if (d.newStock !== undefined && d.newStock !== null && d.newStock !== "") {
          latest.set(key, Math.max(0, Math.floor(n(d.newStock))));
        }
      });
    });

  if (latest.size === 0) return apiStock;

  return apiStock.map((item) => {
    const key = normName(item.name);
    if (!latest.has(key)) return item;
    const eventStock = latest.get(key);
    // Prefer the lower value so a confirm never gets wiped by a stale higher API value
    return { ...item, stock: Math.min(n(item.stock), eventStock) };
  });
};

/* ══════════════════════════════════════════════════
   TOAST COMPONENT
══════════════════════════════════════════════════ */
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);
  return (
    <div className={`sm-toast sm-toast--${type}`}>
      <span className="sm-toast__icon">
        {type === "success" ? "✅" : type === "error" ? "❌" : type === "warning" ? "⚠️" : "ℹ️"}
      </span>
      <span className="sm-toast__message">{message}</span>
      <button className="sm-toast__close" onClick={onClose}>✕</button>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   ALERT BANNER COMPONENT
══════════════════════════════════════════════════ */
const AlertBanner = ({ stock }) => {
  const outOfStock = stock.filter((i) => i.stock === 0);
  const lowStock = stock.filter((i) => i.stock > 0 && i.stock <= i.minStock);
  if (outOfStock.length === 0 && lowStock.length === 0) return null;
  return (
    <div className="sm-alert-banner">
      {outOfStock.length > 0 && (
        <div className="sm-alert sm-alert--out">
          <div className="sm-alert__left">
            <span className="sm-alert__icon">🚫</span>
            <div>
              <div className="sm-alert__title">
                Out of Stock — {outOfStock.length} item{outOfStock.length > 1 ? "s" : ""}
              </div>
              <div className="sm-alert__items">
                {outOfStock.map((i) => (
                  <span key={i.id} className="sm-alert__chip sm-alert__chip--out">
                    {i.emoji} {i.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {lowStock.length > 0 && (
        <div className="sm-alert sm-alert--low">
          <div className="sm-alert__left">
            <span className="sm-alert__icon">⚠️</span>
            <div>
              <div className="sm-alert__title">
                Low Stock — {lowStock.length} item{lowStock.length > 1 ? "s" : ""} below minimum
              </div>
              <div className="sm-alert__items">
                {lowStock.map((i) => (
                  <span key={i.id} className="sm-alert__chip sm-alert__chip--low">
                    {i.emoji} {i.name} ({i.stock} left)
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════
   STOCK EVENT LOG COMPONENT
══════════════════════════════════════════════════ */
const StockEventLog = ({ events, onClear }) => {
  if (events.length === 0) return null;

  return (
    <div className="sm-event-log">
      <div className="sm-event-log__header">
        <h4>📝 Recent Stock Reductions from Frozen Orders</h4>
        <button className="sm-event-log__clear" onClick={onClear}>Clear All</button>
      </div>
      <div className="sm-event-log__list">
        {events.slice().reverse().map((event, idx) => (
          <div key={eventKey(event) || idx} className="sm-event-log__item">
            <div className="sm-event-log__icon">🔒</div>
            <div className="sm-event-log__details">
              <div className="sm-event-log__title">
                <strong>Order Confirmed:</strong> {event.orderName || "Unknown Customer"}
                {event.theater && <span> · {event.theater}</span>}
                {event.eventDate && <span> · {event.eventDate}</span>}
              </div>
              <div className="sm-event-log__time">
                {event.timestamp
                  ? new Date(event.timestamp).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "Just now"}
              </div>
              <div className="sm-event-log__items">
                {(event.items || []).map((item, i) => (
                  <span key={i} className="sm-event-log__chip">
                    {item.emoji} {item.name}: -{item.qty} (Stock: {item.newStock})
                  </span>
                ))}
              </div>
              {event.unmatched && event.unmatched.length > 0 && (
                <div className="sm-event-log__items" style={{ marginTop: 4 }}>
                  {event.unmatched.map((name, i) => (
                    <span
                      key={i}
                      className="sm-event-log__chip"
                      style={{ background: "#fef3c7", color: "#92400e" }}
                    >
                      ⚠️ {name}: not found in stock
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   STOCK ITEM CARD COMPONENT
══════════════════════════════════════════════════ */
const StockItemCard = ({ item, onDelete, onAddStock, onEdit }) => {
  const catInfo = getCategoryInfo(item.category);
  const isLowStock = item.stock > 0 && item.stock <= item.minStock;
  const isOutOfStock = item.stock === 0;
  const stockStatusClass = isOutOfStock
    ? "sm-stock-out"
    : isLowStock
    ? "sm-stock-low"
    : "sm-stock-ok";
  const stockStatusText = isOutOfStock
    ? "Out of Stock"
    : isLowStock
    ? "Low Stock"
    : "In Stock";
  const stockStatusColor = isOutOfStock
    ? "#dc2626"
    : isLowStock
    ? "#d97706"
    : "#16a34a";
  const barMax = Math.max(item.minStock * 3, item.stock, 1);

  return (
    <div
      className={`sm-card ${isOutOfStock ? "sm-card--out" : isLowStock ? "sm-card--low" : ""}`}
    >
      <div className="sm-card__header">
        <div className="sm-card__top">
          <div
            className="sm-card__emoji"
            style={{ background: `${catInfo.color}18`, color: catInfo.color }}
          >
            {item.emoji || catInfo.emoji}
          </div>
          <div className="sm-card__info">
            <h3 className="sm-card__name">{item.name}</h3>
            <span
              className="sm-card__category"
              style={{ background: `${catInfo.color}15`, color: catInfo.color }}
            >
              {catInfo.emoji} {catInfo.label}
            </span>
          </div>
        </div>
        <div className={`sm-card__badge ${stockStatusClass}`}>
          <span
            className="sm-card__badge-dot"
            style={{ background: stockStatusColor }}
          />
          {stockStatusText}
        </div>
      </div>
      <div className="sm-card__divider" />
      <div className="sm-card__body">
        <div className="sm-card__price-row">
          <span className="sm-card__price-label">Price</span>
          <span className="sm-card__price-value">{money(item.price)}</span>
        </div>
        <div className="sm-card__stock-section">
          <div className="sm-card__stock-header">
            <span className="sm-card__stock-label">Current Stock</span>
            <span className="sm-card__stock-number" style={{ color: stockStatusColor }}>
              {item.stock}
            </span>
          </div>
          <div className="sm-card__stock-bar">
            <div
              className="sm-card__stock-bar-fill"
              style={{
                width: `${Math.min(100, (item.stock / barMax) * 100)}%`,
                background: stockStatusColor,
              }}
            />
          </div>
          <div className="sm-card__stock-meta">
            <span>Min: {item.minStock}</span>
          </div>
        </div>
        {(isOutOfStock || isLowStock) && (
          <div
            className={`sm-card__inline-alert ${
              isOutOfStock
                ? "sm-card__inline-alert--out"
                : "sm-card__inline-alert--low"
            }`}
          >
            {isOutOfStock
              ? "🚫 This item is out of stock. Please restock immediately."
              : `⚠️ Only ${item.stock} unit${item.stock > 1 ? "s" : ""} left — below minimum of ${item.minStock}.`}
          </div>
        )}
      </div>
      <div className="sm-card__divider" />
      <div className="sm-card__footer">
        <button
          className="sm-card__action-btn sm-card__action-btn--stock"
          onClick={() => onAddStock(item)}
        >
          ➕ Add Stock
        </button>
        <button
          className="sm-card__action-btn sm-card__action-btn--edit"
          onClick={() => onEdit(item)}
          title="Edit item details"
        >
          ✏️ Edit
        </button>
        <button
          className="sm-card__action-btn sm-card__action-btn--danger"
          onClick={() => onDelete(item)}
        >
          🗑️ Remove
        </button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   ADD/EDIT ITEM MODAL
══════════════════════════════════════════════════ */
const ItemModal = ({ item, onSave, onClose }) => {
  const isEditing = !!item;
  const [formData, setFormData] = useState(
    item
      ? {
          name: item.name,
          category: item.category,
          price: item.price,
          stock: item.stock,
          minStock: item.minStock,
          emoji: item.emoji,
        }
      : { name: "", category: "cakes", price: 0, stock: 0, minStock: 5, emoji: "" }
  );

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("Item name is required.");
      return;
    }
    const catInfo = getCategoryInfo(formData.category);
    onSave(
      {
        ...formData,
        name: formData.name.trim(),
        emoji: formData.emoji || catInfo.emoji,
        price: Math.max(0, n(formData.price)),
        stock: Math.max(0, Math.floor(n(formData.stock))),
        minStock: Math.max(1, Math.floor(n(formData.minStock))),
      },
      isEditing
    );
    onClose();
  };

  return (
    <div className="sm-modal-overlay" onClick={onClose}>
      <div className="sm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sm-modal__header">
          <h2>{isEditing ? "✏️ Edit Item" : "➕ Add New Item"}</h2>
          <button className="sm-modal__close" onClick={onClose}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className="sm-modal__body">
          <div className="sm-form-group">
            <label>Item Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="e.g. Chocolate Truffle Cake"
              required
              autoFocus
            />
          </div>
          <div className="sm-form-group">
            <label>Category</label>
            <select
              value={formData.category}
              onChange={(e) => handleChange("category", e.target.value)}
            >
              {CATEGORIES.filter((c) => c.key !== "all").map((cat) => (
                <option key={cat.key} value={cat.key}>
                  {cat.emoji} {cat.label}
                </option>
              ))}
            </select>
          </div>
          <div className="sm-form-row">
            <div className="sm-form-group">
              <label>Price (₹)</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => handleChange("price", e.target.value)}
                min="0"
                placeholder="0"
              />
            </div>
            <div className="sm-form-group">
              <label>Stock</label>
              <input
                type="number"
                value={formData.stock}
                onChange={(e) => handleChange("stock", e.target.value)}
                min="0"
                placeholder="0"
              />
            </div>
          </div>
          <div className="sm-form-group">
            <label>Minimum Stock Alert Level</label>
            <input
              type="number"
              value={formData.minStock}
              onChange={(e) => handleChange("minStock", e.target.value)}
              min="1"
              placeholder="5"
            />
          </div>
          <div className="sm-modal__footer">
            <button type="button" className="sm-modal__btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="sm-modal__btn-save">
              {isEditing ? "💾 Save Changes" : "➕ Add Item"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   MAIN STOCK MANAGEMENT COMPONENT
══════════════════════════════════════════════════ */
const StockManagement = () => {
  const [stock, setStock] = useState(() => {
    // On first paint: load local cache then immediately apply any saved confirm events
    const base = loadStock();
    const events = loadStockEvents();
    const merged = applyAllEventsToStock(base, events);
    saveStock(merged);
    return merged;
  });
  const [stockEvents, setStockEvents] = useState(loadStockEvents);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [filterStock, setFilterStock] = useState("all");
  const [itemModal, setItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [toast, setToast] = useState(null);
  const [viewMode, setViewMode] = useState("cards");
  const [loading, setLoading] = useState(true);

  const lastEventsCountRef = useRef(loadStockEvents().length);
  const appliedEventKeysRef = useRef(new Set());
  const protectUntilRef = useRef(0); // don't let stale API raise stock until this time

  const apiCall = useCallback(async (endpoint, options = {}) => {
    try {
      const response = await fetch(`${STOCK_API}${endpoint}`, {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(options.headers || {}),
        },
        ...options,
      });
      const resJson = await response.json().catch(() => ({}));
      return { ok: response.ok, status: response.status, data: resJson };
    } catch (error) {
      console.warn(`[Stock API Offline/Error] ${endpoint}:`, error.message);
      return { ok: false, status: 0, error };
    }
  }, []);

  /** Force card stock update + persist + short API-protect window */
  const commitStock = useCallback((updater, { protectMs = 0 } = {}) => {
    setStock((prev) => {
      const nextRaw = typeof updater === "function" ? updater(prev) : updater;
      const next = (nextRaw || []).map(normalizeItem).filter(Boolean);
      saveStock(next);
      if (protectMs > 0) {
        protectUntilRef.current = Date.now() + protectMs;
      }
      return next;
    });
  }, []);

  const fetchStockFromAPI = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        let res = await apiCall("/all");
        let itemsList = [];

        if (res.ok && Array.isArray(res.data?.data)) {
          itemsList = res.data.data;
        } else {
          const categoryResults = await Promise.all(
            CATEGORIES.filter((c) => c.key !== "all").map((cat) =>
              apiCall(`/${cat.key}`)
            )
          );
          categoryResults.forEach((cRes) => {
            if (cRes.ok && Array.isArray(cRes.data?.data)) {
              itemsList.push(...cRes.data.data);
            }
          });
        }

        if (itemsList.length > 0) {
          let normalizedList = itemsList.map(normalizeItem).filter(Boolean);

          // Keep card counts correct vs recent confirm events (banner already has truth)
          const events = loadStockEvents();
          normalizedList = mergeApiStockWithRecentEvents(normalizedList, events);

          // During protect window after a local confirm apply, never raise stock
          if (Date.now() < protectUntilRef.current) {
            setStock((prev) => {
              const prevMap = new Map(prev.map((p) => [normName(p.name), n(p.stock)]));
              const merged = normalizedList.map((item) => {
                const key = normName(item.name);
                if (!prevMap.has(key)) return item;
                return { ...item, stock: Math.min(n(item.stock), prevMap.get(key)) };
              });
              saveStock(merged);
              return merged;
            });
          } else {
            saveStock(normalizedList);
            setStock(normalizedList);
          }
        }
      } catch (err) {
        console.error("API Fetch Error:", err);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [apiCall]
  );

  /* Initial load + live poll */
  useEffect(() => {
    fetchStockFromAPI(false);
    const intervalId = setInterval(() => fetchStockFromAPI(true), 8000);
    return () => clearInterval(intervalId);
  }, [fetchStockFromAPI]);

  /* Persist stock whenever it changes */
  useEffect(() => {
    saveStock(stock);
  }, [stock]);

  /* ════════════════════════════════════════════════════
     APPLY ORDER-CONFIRM → CARD CURRENT STOCK
  ════════════════════════════════════════════════════ */
  useEffect(() => {
    const applyOrderConfirmEvent = (stockEvent, { toastOnApply = true } = {}) => {
      if (!stockEvent) return;

      const key = eventKey(stockEvent);
      const items = Array.isArray(stockEvent.items) ? stockEvent.items : [];

      // Always force card numbers from this event's newStock
      if (items.length > 0) {
        commitStock(
          (prev) => applyDeductionsToStock(prev, items),
          { protectMs: 6000 }
        );
      }

      // Event log UI (dedupe)
      setStockEvents((prev) => {
        const already = prev.some((e) => eventKey(e) === key);
        if (already) {
          appliedEventKeysRef.current.add(key);
          return prev;
        }
        const next = [...prev, stockEvent];
        saveStockEvents(next);
        lastEventsCountRef.current = next.length;
        appliedEventKeysRef.current.add(key);
        return next;
      });

      if (toastOnApply && items.length > 0 && !appliedEventKeysRef.current.has(`toast:${key}`)) {
        appliedEventKeysRef.current.add(`toast:${key}`);
        const customerName = stockEvent.orderName || "a frozen order";
        setToast({
          message: `Stock auto-reduced! ${customerName}'s order confirmed — ${items.length} item(s) updated.`,
          type: "success",
        });
      }

      // Pull Mongo shortly after (merge still protects card counts)
      setTimeout(() => fetchStockFromAPI(true), 500);
      setTimeout(() => fetchStockFromAPI(true), 2500);
    };

    const handleCustomEvent = (e) => {
      const stockEvent = e?.detail?.event;
      if (stockEvent) {
        applyOrderConfirmEvent(stockEvent, { toastOnApply: true });
        return;
      }

      // Fallback: localStorage
      const newStock = loadStock();
      const events = loadStockEvents();
      commitStock(() => applyAllEventsToStock(newStock, events), { protectMs: 4000 });

      if (events.length > lastEventsCountRef.current) {
        lastEventsCountRef.current = events.length;
        setStockEvents(events);
        const latest = events[events.length - 1];
        if (latest) applyOrderConfirmEvent(latest, { toastOnApply: true });
      }
    };

    const handleStorageEvent = (e) => {
      if (e.key === STOCK_EVENTS_KEY) {
        const newEvents = loadStockEvents();
        setStockEvents(newEvents);
        if (newEvents.length > lastEventsCountRef.current) {
          lastEventsCountRef.current = newEvents.length;
          const latest = newEvents[newEvents.length - 1];
          if (latest) applyOrderConfirmEvent(latest, { toastOnApply: true });
        } else {
          // Even if count same, re-apply absolute newStocks onto cards
          commitStock(
            (prev) => applyAllEventsToStock(prev, newEvents),
            { protectMs: 4000 }
          );
        }
      }

      if (e.key === STORAGE_KEY) {
        const fromStorage = loadStock();
        const events = loadStockEvents();
        commitStock(
          () => applyAllEventsToStock(fromStorage, events),
          { protectMs: 3000 }
        );
      }
    };

    window.addEventListener("stockUpdated", handleCustomEvent);
    window.addEventListener("storage", handleStorageEvent);

    // Same-tab / late navigation: poll events + re-apply newStock onto cards
    const pollId = setInterval(() => {
      const newEvents = loadStockEvents();

      if (newEvents.length !== lastEventsCountRef.current) {
        const grew = newEvents.length > lastEventsCountRef.current;
        lastEventsCountRef.current = newEvents.length;
        setStockEvents(newEvents);

        if (grew) {
          const latest = newEvents[newEvents.length - 1];
          if (latest) applyOrderConfirmEvent(latest, { toastOnApply: true });
        } else {
          commitStock(
            (prev) => applyAllEventsToStock(prev, newEvents),
            { protectMs: 3000 }
          );
        }
      } else if (newEvents.length > 0) {
        // Count unchanged but cards may still be stale after navigation
        setStock((prev) => {
          const next = applyAllEventsToStock(prev, newEvents);
          // only write if something actually changed
          const prevSig = prev.map((i) => `${i.id}:${i.stock}`).join("|");
          const nextSig = next.map((i) => `${i.id}:${i.stock}`).join("|");
          if (prevSig === nextSig) return prev;
          saveStock(next);
          protectUntilRef.current = Date.now() + 3000;
          return next;
        });
      }
    }, 1000);

    // One-shot on mount: ensure events already in storage reduce cards
    const bootEvents = loadStockEvents();
    if (bootEvents.length > 0) {
      commitStock(
        (prev) => applyAllEventsToStock(prev, bootEvents),
        { protectMs: 5000 }
      );
      bootEvents.forEach((ev) => appliedEventKeysRef.current.add(eventKey(ev)));
    }

    return () => {
      window.removeEventListener("stockUpdated", handleCustomEvent);
      window.removeEventListener("storage", handleStorageEvent);
      clearInterval(pollId);
    };
  }, [commitStock, fetchStockFromAPI]);

  const filtered = useMemo(() => {
    let items = [...stock];
    if (activeCategory !== "all") {
      items = items.filter((i) => i.category === activeCategory);
    }
    if (filterStock === "low") {
      items = items.filter((i) => i.stock > 0 && i.stock <= i.minStock);
    } else if (filterStock === "out") {
      items = items.filter((i) => i.stock === 0);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      items = items.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          i.category.toLowerCase().includes(q)
      );
    }
    return items;
  }, [stock, activeCategory, filterStock, search]);

  const stats = useMemo(() => {
    const totalItems = stock.length;
    const totalValue = stock.reduce((a, i) => a + n(i.price) * n(i.stock), 0);
    const lowStock = stock.filter((i) => i.stock > 0 && i.stock <= i.minStock).length;
    const outOfStock = stock.filter((i) => i.stock === 0).length;
    return { totalItems, totalValue, lowStock, outOfStock };
  }, [stock]);

  const handleAddStock = async (item) => {
    const input = window.prompt(
      `How many units of "${item.name}" would you like to add?\n\nCurrent stock: ${item.stock}`,
      "1"
    );
    if (input === null) return;
    const qty = Math.floor(Number(input));
    if (!Number.isFinite(qty) || qty <= 0) {
      setToast({ message: "Please enter a valid positive number.", type: "error" });
      return;
    }

    const newTotal = n(item.stock) + qty;
    // Manual restock: clear protect so higher stock is allowed
    protectUntilRef.current = 0;
    commitStock((prev) =>
      prev.map((s) =>
        s.id === item.id || s._id === item._id ? { ...s, stock: newTotal } : s
      )
    );
    setToast({
      message: `Added ${qty} unit${qty > 1 ? "s" : ""} to "${item.name}". New stock: ${newTotal}.`,
      type: "success",
    });

    const res = await apiCall(`/${item.category}/${item.id || item._id}/stock`, {
      method: "PATCH",
      body: JSON.stringify({ delta: qty, note: `Manual restock: +${qty}` }),
    });

    if (res.ok && res.data?.data) {
      const updated = normalizeItem(res.data.data);
      commitStock((prev) =>
        prev.map((s) =>
          s.id === updated.id || s._id === updated._id ? updated : s
        )
      );
    }
  };

  const handleDelete = async (item) => {
    if (
      !window.confirm(
        `Remove "${item.name}" from stock globally across all browsers?`
      )
    )
      return;

    commitStock((prev) =>
      prev.filter((i) => i.id !== item.id && i._id !== item._id)
    );
    setToast({ message: `"${item.name}" removed from stock.`, type: "success" });

    await apiCall(`/${item.category}/${item.id || item._id}`, {
      method: "DELETE",
    });
  };

  const handleAdd = () => {
    setEditingItem(null);
    setItemModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setItemModal(true);
  };

  const handleSaveItem = async (data, isEditing) => {
    if (isEditing && editingItem) {
      protectUntilRef.current = 0;
      commitStock((prev) =>
        prev.map((item) =>
          item.id === editingItem.id || item._id === editingItem._id
            ? normalizeItem({ ...item, ...data })
            : item
        )
      );
      setToast({ message: `"${data.name}" updated successfully!`, type: "success" });

      const res = await apiCall(
        `/${data.category}/${editingItem.id || editingItem._id}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        }
      );

      if (res.ok && res.data?.data) {
        const updated = normalizeItem(res.data.data);
        commitStock((prev) =>
          prev.map((s) =>
            s.id === updated.id || s._id === updated._id ? updated : s
          )
        );
      }
    } else {
      const tempItem = normalizeItem({ ...data, id: `${Date.now()}` });
      commitStock((prev) => [tempItem, ...prev]);
      setToast({ message: `"${data.name}" added to stock!`, type: "success" });

      const res = await apiCall(`/${data.category}`, {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (res.ok && res.data?.data) {
        const created = normalizeItem(res.data.data);
        commitStock((prev) =>
          prev.map((s) => (s.id === tempItem.id ? created : s))
        );
      }
    }
    setEditingItem(null);
  };

  const handleExport = () => {
    const data = JSON.stringify(stock, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stock-export-${new Date().toISOString().split("T")[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setToast({ message: "Stock data exported successfully!", type: "success" });
  };

  const handleImport = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const data = JSON.parse(ev.target.result);
          if (Array.isArray(data)) {
            const normalized = data.map(normalizeItem).filter(Boolean);
            protectUntilRef.current = 0;
            commitStock(() => normalized);
            setToast({ message: "Importing across all browsers...", type: "info" });

            for (const item of normalized) {
              await apiCall(`/${item.category}`, {
                method: "POST",
                body: JSON.stringify(item),
              });
            }
            fetchStockFromAPI(true);
            setToast({
              message: "Stock data imported and synced successfully!",
              type: "success",
            });
          } else {
            setToast({ message: "Invalid file format.", type: "error" });
          }
        } catch (err) {
          setToast({ message: "Failed to parse import file.", type: "error" });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleClearEvents = () => {
    setStockEvents([]);
    saveStockEvents([]);
    appliedEventKeysRef.current = new Set();
    lastEventsCountRef.current = 0;
    protectUntilRef.current = 0;
    setToast({ message: "Stock events cleared.", type: "success" });
    // Re-sync pure Mongo values after clearing event overlay
    fetchStockFromAPI(true);
  };

  if (loading && stock.length === 0) {
    return (
      <div className="sm-loading">
        <div className="sm-spinner" />
        <p>Syncing stock management from database...</p>
      </div>
    );
  }

  return (
    <div className="sm-page">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {itemModal && (
        <ItemModal
          item={editingItem}
          onSave={handleSaveItem}
          onClose={() => {
            setItemModal(false);
            setEditingItem(null);
          }}
        />
      )}

      <div className="sm-header">
        <div className="sm-header__left">
          <div className="sm-header__icon">📦</div>
          <div>
            <h1>Stock Management</h1>
            <p>
              Manage inventory for add-ons, cakes, gifts &amp; services across all
              browsers
            </p>
          </div>
        </div>
        <div className="sm-header__right">
          <button className="sm-btn sm-btn--import" onClick={handleImport}>
            📥 Import
          </button>
          <button className="sm-btn sm-btn--export" onClick={handleExport}>
            📤 Export
          </button>
          <button className="sm-btn sm-btn--add" onClick={handleAdd}>
            ➕ Add Item
          </button>
        </div>
      </div>

      <AlertBanner stock={stock} />

      <StockEventLog events={stockEvents} onClear={handleClearEvents} />

      <div className="sm-stats">
        <div className="sm-stat-card sm-stat--total">
          <div className="sm-stat__icon">📦</div>
          <div className="sm-stat__data">
            <span className="sm-stat__value">{stats.totalItems}</span>
            <span className="sm-stat__label">Total Items</span>
          </div>
        </div>
        <div className="sm-stat-card sm-stat--value">
          <div className="sm-stat__icon">💰</div>
          <div className="sm-stat__data">
            <span className="sm-stat__value">{money(stats.totalValue)}</span>
            <span className="sm-stat__label">Total Stock Value</span>
          </div>
        </div>
        <div
          className={`sm-stat-card sm-stat--low ${
            stats.lowStock > 0 ? "sm-stat--alert-pulse" : ""
          }`}
          onClick={() => stats.lowStock > 0 && setFilterStock("low")}
          style={{ cursor: stats.lowStock > 0 ? "pointer" : "default" }}
          title={stats.lowStock > 0 ? "Click to filter low stock items" : ""}
        >
          <div className="sm-stat__icon">⚠️</div>
          <div className="sm-stat__data">
            <span className="sm-stat__value">{stats.lowStock}</span>
            <span className="sm-stat__label">Low Stock Alerts</span>
          </div>
          {stats.lowStock > 0 && (
            <span className="sm-stat__badge-pill">Click to view</span>
          )}
        </div>
        <div
          className={`sm-stat-card sm-stat--out ${
            stats.outOfStock > 0 ? "sm-stat--alert-pulse-red" : ""
          }`}
          onClick={() => stats.outOfStock > 0 && setFilterStock("out")}
          style={{ cursor: stats.outOfStock > 0 ? "pointer" : "default" }}
          title={stats.outOfStock > 0 ? "Click to filter out of stock items" : ""}
        >
          <div className="sm-stat__icon">🚫</div>
          <div className="sm-stat__data">
            <span className="sm-stat__value">{stats.outOfStock}</span>
            <span className="sm-stat__label">Out of Stock</span>
          </div>
          {stats.outOfStock > 0 && (
            <span className="sm-stat__badge-pill sm-stat__badge-pill--red">
              Click to view
            </span>
          )}
        </div>
      </div>

      <div className="sm-filters">
        <div className="sm-filters__left">
          <div className="sm-search">
            <span className="sm-search__icon">🔍</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search stock items..."
            />
            {search && (
              <button className="sm-search__clear" onClick={() => setSearch("")}>
                ✕
              </button>
            )}
          </div>
          <div className="sm-stock-filter">
            <button
              className={`sm-stock-filter__btn ${filterStock === "all" ? "active" : ""}`}
              onClick={() => setFilterStock("all")}
            >
              All
            </button>
            <button
              className={`sm-stock-filter__btn sm-stock-filter__btn--low ${
                filterStock === "low" ? "active" : ""
              }`}
              onClick={() => setFilterStock("low")}
            >
              ⚠️ Low
            </button>
            <button
              className={`sm-stock-filter__btn sm-stock-filter__btn--out ${
                filterStock === "out" ? "active" : ""
              }`}
              onClick={() => setFilterStock("out")}
            >
              🚫 Out
            </button>
          </div>
        </div>
        <div className="sm-filters__right">
          <div className="sm-view-toggle">
            <button
              className={`sm-view-toggle__btn ${viewMode === "cards" ? "active" : ""}`}
              onClick={() => setViewMode("cards")}
              title="Card view"
            >
              ▦
            </button>
            <button
              className={`sm-view-toggle__btn ${viewMode === "table" ? "active" : ""}`}
              onClick={() => setViewMode("table")}
              title="Table view"
            >
              ☰
            </button>
          </div>
        </div>
      </div>

      <div className="sm-tabs">
        {CATEGORIES.map((cat) => {
          const count =
            cat.key === "all"
              ? stock.length
              : stock.filter((i) => i.category === cat.key).length;
          const catLow =
            cat.key === "all"
              ? stock.filter((i) => i.stock > 0 && i.stock <= i.minStock).length
              : stock.filter(
                  (i) =>
                    i.category === cat.key &&
                    i.stock > 0 &&
                    i.stock <= i.minStock
                ).length;
          const catOut =
            cat.key === "all"
              ? stock.filter((i) => i.stock === 0).length
              : stock.filter((i) => i.category === cat.key && i.stock === 0)
                  .length;
          return (
            <button
              key={cat.key}
              className={`sm-tab ${activeCategory === cat.key ? "active" : ""}`}
              onClick={() => setActiveCategory(cat.key)}
              style={{ "--cat-color": cat.color }}
            >
              <span className="sm-tab__emoji">{cat.emoji}</span>
              <span className="sm-tab__label">{cat.label}</span>
              <span className="sm-tab__count">{count}</span>
              {catOut > 0 && (
                <span className="sm-tab__alert sm-tab__alert--out">{catOut}</span>
              )}
              {catLow > 0 && catOut === 0 && (
                <span className="sm-tab__alert sm-tab__alert--low">{catLow}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="sm-results">
        <span>
          Showing <b>{filtered.length}</b> of <b>{stock.length}</b> items
        </span>
        {filterStock !== "all" && (
          <button
            className="sm-results__clear"
            onClick={() => setFilterStock("all")}
          >
            ✕ Clear filter
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="sm-empty">
          <div className="sm-empty__icon">📭</div>
          <h3>No items found</h3>
          <p>
            {search
              ? `No items match "${search}".`
              : filterStock !== "all"
              ? "No items match this stock filter."
              : "No items in this category yet."}
          </p>
          <button
            className="sm-btn sm-btn--add"
            onClick={handleAdd}
            style={{ marginTop: 16 }}
          >
            ➕ Add New Item
          </button>
        </div>
      ) : viewMode === "cards" ? (
        <div className="sm-grid">
          {filtered.map((item) => (
            <StockItemCard
              key={`${item.id}-${item.stock}`}
              item={item}
              onDelete={handleDelete}
              onAddStock={handleAddStock}
              onEdit={handleEdit}
            />
          ))}
        </div>
      ) : (
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Item</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, idx) => {
                const catInfo = getCategoryInfo(item.category);
                const isLowStock = item.stock > 0 && item.stock <= item.minStock;
                const isOutOfStock = item.stock === 0;
                const statusColor = isOutOfStock
                  ? "#dc2626"
                  : isLowStock
                  ? "#d97706"
                  : "#16a34a";
                const statusText = isOutOfStock
                  ? "Out of Stock"
                  : isLowStock
                  ? "Low Stock"
                  : "In Stock";
                return (
                  <tr
                    key={`${item.id}-${item.stock}`}
                    className={
                      isOutOfStock
                        ? "sm-table__row--out"
                        : isLowStock
                        ? "sm-table__row--low"
                        : ""
                    }
                  >
                    <td className="sm-table__idx">{idx + 1}</td>
                    <td>
                      <div className="sm-table__item-name">
                        <span className="sm-table__emoji">
                          {item.emoji || catInfo.emoji}
                        </span>
                        <b>{item.name}</b>
                      </div>
                    </td>
                    <td>
                      <span
                        className="sm-table__cat-badge"
                        style={{
                          background: `${catInfo.color}18`,
                          color: catInfo.color,
                        }}
                      >
                        {catInfo.label}
                      </span>
                    </td>
                    <td className="sm-table__price">{money(item.price)}</td>
                    <td>
                      <span
                        className="sm-table__stock-num"
                        style={{ color: statusColor }}
                      >
                        {item.stock}
                      </span>
                    </td>
                    <td>
                      <span
                        className="sm-table__status"
                        style={{
                          background: `${statusColor}15`,
                          color: statusColor,
                          border: `1px solid ${statusColor}30`,
                        }}
                      >
                        {statusText}
                      </span>
                    </td>
                    <td>
                      <div className="sm-table__actions">
                        <button onClick={() => handleAddStock(item)} title="Add Stock">
                          ➕
                        </button>
                        <button onClick={() => handleEdit(item)} title="Edit">
                          ✏️
                        </button>
                        <button onClick={() => handleDelete(item)} title="Delete">
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="sm-footer">
        <span>
          💡 Live sync active. Confirming a frozen order instantly reduces{" "}
          <b>Current Stock</b> on every matching card.
        </span>
      </div>
    </div>
  );
};

export default StockManagement;